import BaseChart from './BaseChart';
import { CHART_ANIMATIONS } from '../config/animations';

/**
 * Line chart implementation using VChart
 */
class LineChart extends BaseChart {
  /**
   * Get chart specification for line chart
   * @returns {Object} Chart specification
   */
  getChartSpecification() {
    const commonConfig = this.getCommonConfig();

    // Determine fields for x and y axes — auto-detect from data when not provided
    const xField = this.options.xField || this._resolveNameField();
    let yField = this.options.yField || this._resolveValueField();
    let seriesField = this.options.seriesField;
    let data;

    // Handle array yField — pivot wide-format data to long format for multi-series
    if (Array.isArray(yField)) {
      const pivoted = this._pivotWideToLong(xField, yField);
      data = pivoted.data;
      seriesField = pivoted.seriesField;
      yField = pivoted.yField;
    } else {
      data = this.prepareData();
    }

    return {
      type: 'line',
      data,
      xField,
      yField,
      seriesField,
      
      // Line specific styling
      line: {
        style: {
          lineWidth: 2,
          smooth: this.options.smooth !== false,
          ...this.options.lineStyle
        },
        state: {
          hover: {
            lineWidth: 3
          },
          ...this.options.lineState
        }
      },
      
      // Point markers configuration
      point: {
        visible: this.options.showPoints !== false,
        style: {
          size: 4,
          fillOpacity: 1,
          stroke: '#fff',
          lineWidth: 1,
          ...this.options.pointStyle
        },
        state: {
          hover: {
            size: 6,
            stroke: '#fff',
            lineWidth: 2
          },
          ...this.options.pointState
        },
        ...this.options.point
      },
      
      // Line label configuration
      label: {
        visible: this.options.showLabels === true,
        position: this.options.labelPosition || 'top',
        style: {
          fontSize: 12,
          fontWeight: 'normal',
          ...this.options.labelStyle
        },
        formatter: this.options.labelFormatter
      },
      
      // Axes configuration
      axes: this.getAxesConfig(),
      
      // Crosshair for hover interactions
      crosshair: {
        xField: { visible: true },
        yField: { visible: true }
      },
      
      // Animation configuration specific to line charts
      animationAppear: {
        ...CHART_ANIMATIONS.line.appear,
        ...this.options.animationAppear
      },
      
      // Common configuration from BaseChart
      ...commonConfig
    };
  }
  
  /**
   * Get axes configuration
   * @returns {Array} Axes configuration
   */
  getAxesConfig() {
    const xField = this.options.xField || this._resolveNameField();
    const xAxisType = this._detectXAxisType(xField);

    // Default axes configuration
    const axesConfig = [
      {
        orient: 'bottom',
        type: xAxisType,
        label: {
          visible: true,
          style: {
            fontSize: 12,
            ...this.options.xAxisLabelStyle
          },
          formatter: this.options.xAxisLabelFormatter
        },
        title: {
          visible: !!this.options.xAxisTitle,
          text: this.options.xAxisTitle || '',
          style: {
            fontSize: 14,
            fontWeight: 'bold',
            ...this.options.xAxisTitleStyle
          }
        },
        ...this.options.xAxis
      },
      {
        orient: 'left',
        type: 'linear',
        label: {
          visible: true,
          style: {
            fontSize: 12,
            ...this.options.yAxisLabelStyle
          },
          formatter: this.options.yAxisLabelFormatter
        },
        title: {
          visible: !!this.options.yAxisTitle,
          text: this.options.yAxisTitle || '',
          style: {
            fontSize: 14,
            fontWeight: 'bold',
            ...this.options.yAxisTitleStyle
          }
        },
        grid: {
          visible: true,
          style: {
            stroke: '#E8E8E8',
            lineWidth: 1,
            lineDash: [4, 4],
            ...this.options.gridStyle
          }
        },
        ...this.options.yAxis
      }
    ];
    
    return axesConfig;
  }
}

export default LineChart; 